/**
 * 价格数据处理工具函数
 */
import type { PriceData } from '../types';
import type { TimeGranularity } from './dateUtils';
import dayjs from './dayjs';
/**
 * 按时间粒度聚合价格数据
 * 验证需求：6.5
 * 属性9：时间粒度聚合正确性
 */
export function aggregatePriceData(
  data: PriceData[],
  granularity: TimeGranularity
): PriceData[] {
  // 【修复点】增加 Array.isArray 检查，防止非数组数据导致崩溃
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }
  // 按时间粒度分组
  const groups = new Map<string, PriceData[]>();
  data.forEach((item) => {
    const date = dayjs(item.date);
    let key: string;
    switch (granularity) {
      case 'day':
        key = date.format('YYYY-MM-DD');
        break;
      case 'week':
        key = `${date.year()}-W${date.week()}`;
        break;
      case 'month':
        key = date.format('YYYY-MM');
        break;
      case 'quarter':
        key = `${date.year()}-Q${date.quarter()}`;
        break;
      case 'year':
        key = date.format('YYYY');
        break;
      default:
        key = date.format('YYYY-MM-DD');
    }
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(item);
  });
  // 聚合每组数据（取平均值）
  const aggregated: PriceData[] = [];
  groups.forEach((items, key) => {
    const avgPriceCny = items.reduce((sum, item) => sum + item.price_cny, 0) / items.length;
    const avgPriceUsd = items.reduce((sum, item) => sum + item.price_usd, 0) / items.length;
    const totalVolume = items.reduce((sum, item) => sum + (item.volume || 0), 0);
    // 使用组内第一个日期作为代表日期
    aggregated.push({
      ...items[0],
      date: items[0].date,
      price_cny: avgPriceCny,
      price_usd: avgPriceUsd,
      volume: totalVolume,
    });
  });
  // 按日期排序
  return aggregated.sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
}
/**
 * 计算价格变化百分比
 */
export function calculatePriceChange(current: number, previous: number): number {
  if (previous === 0) return 0;
  return ((current - previous) / previous) * 100;
}
/**
 * 格式化价格显示
 */
export function formatPrice(price: number, currency: 'CNY' | 'USD' = 'CNY'): string {
  if (currency === 'CNY') {
    return `¥${price.toFixed(2)}`;
  }
  return `$${price.toFixed(2)}`;
}
